import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import {
  DEFAULT_MAP_CONTENT_MEMBER_LAYERS,
  DEFAULT_MAP_CONTENT_PROVIDER_OPTIONS,
  DEFAULT_MAP_STYLE_BOUNDARY_LAYERS,
  DEFAULT_MAP_STYLE_COUNTY_SHADING,
  DEFAULT_MAP_STYLE_SOLID_FILL_COLOR,
  MultiMapExportState
} from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import {
  MultiMapExportAdequacyOutcome,
  MultiMapExportBoundaryLayer,
  MultiMapExportCountyShading,
  MultiMapExportMemberLayer,
  MultiMapExportShadingSwatch,
} from "@app/projects/network-assessment/multi-map-export/interfaces";
import {
  MAP_STYLE_ADEQUACY_SHADING_OPTIONS,
  MAP_STYLE_COUNTY_STATUS_SHADING_OPTIONS,
  MAP_STYLE_SOLID_FILL_OPTIONS,
} from "@app/projects/network-assessment/multi-map-export/utils/mapStyleOptions";
import { RegionSetStatusType } from "@app/projects/enumerators";

// Excludes the group keys (Network/Market/Prospect) and legacy values (Target/Excluded) that
// selectedMapContentProviderOptions never actually holds.
export type ProviderLeafOption = Exclude<
  ProvidersFilterOptions,
  ProvidersFilterOptions.Network | ProvidersFilterOptions.Market | ProvidersFilterOptions.Prospect | ProvidersFilterOptions.Target | ProvidersFilterOptions.Excluded
>;

export type MapExportSampleContentLayer = MultiMapExportMemberLayer | ProviderLeafOption;

const NON_LEAF_PROVIDER_OPTIONS = new Set<ProvidersFilterOptions>([
  ProvidersFilterOptions.Network,
  ProvidersFilterOptions.Market,
  ProvidersFilterOptions.Prospect,
  ProvidersFilterOptions.Target,
  ProvidersFilterOptions.Excluded,
]);

const isProviderLeafOption = (option: ProvidersFilterOptions): option is ProviderLeafOption =>
  !NON_LEAF_PROVIDER_OPTIONS.has(option);

export const getVisibleBoundaryLayers = (selections?: MultiMapExportState): readonly MultiMapExportBoundaryLayer[] =>
  selections?.selectedBoundaryLayers ?? DEFAULT_MAP_STYLE_BOUNDARY_LAYERS;

export const getVisibleContentLayers = (selections?: MultiMapExportState): readonly MapExportSampleContentLayer[] => [
  ...(selections?.selectedMapContentMemberLayers ?? DEFAULT_MAP_CONTENT_MEMBER_LAYERS),
  ...(selections?.selectedMapContentProviderOptions ?? DEFAULT_MAP_CONTENT_PROVIDER_OPTIONS).filter(isProviderLeafOption)
];

// The sample county's made-up results, so the data-driven modes have something to show.
export const SAMPLE_ADEQUACY_OUTCOME = MultiMapExportAdequacyOutcome.FailingAccessRequirement;
export const SAMPLE_COUNTY_STATUS = RegionSetStatusType.Active;

const colorOf = <TValue>(options: readonly MultiMapExportShadingSwatch<TValue>[], value: TValue): string | undefined =>
  options.find((option) => option.value === value)?.color;

export const getCountyShading = (selections?: MultiMapExportState): MultiMapExportCountyShading =>
  selections?.selectedCountyShading ?? DEFAULT_MAP_STYLE_COUNTY_SHADING;

/** Only No Shading draws on the Mapbox base map; the other modes draw on a plain background. */
export const usesBaseMapImage = (selections?: MultiMapExportState): boolean =>
  getCountyShading(selections) === MultiMapExportCountyShading.NoShading;

/** The sample county's fill, or undefined when the county is not filled. */
export const getSampleCountyFill = (selections?: MultiMapExportState): string | undefined => {
  switch (getCountyShading(selections)) {
    case MultiMapExportCountyShading.SolidColor:
      return colorOf(MAP_STYLE_SOLID_FILL_OPTIONS, selections?.selectedSolidFillColor ?? DEFAULT_MAP_STYLE_SOLID_FILL_COLOR);
    case MultiMapExportCountyShading.AdequacyStatus:
      return colorOf(MAP_STYLE_ADEQUACY_SHADING_OPTIONS, SAMPLE_ADEQUACY_OUTCOME);
    case MultiMapExportCountyShading.CountyStatus:
      return colorOf(MAP_STYLE_COUNTY_STATUS_SHADING_OPTIONS, SAMPLE_COUNTY_STATUS);
    default:
      return undefined;
  }
};
